import { UseFormReturn } from "react-hook-form";
import { InviteStaffMemberFormValues } from "./useInviteStaffMemberForm";
import {
  EnumUserAccessOperatorsRole,
  useUserCreateInviteMutation,
  useUserApproveAccessMutation,
  useUserRevokeInviteMutation,
  OperatorStaffQuery,
  Scalars,
  Exact,
  OrganizationStaffQuery,
  EnumEntityReferenceType,
  OperatorStaffDocument,
  OrganizationStaffDocument,
  RoleType,
  useUserUpdateMutation,
  UserWithPictureTc,
  UserAccessOperators,
  ContactRole,
  useOperatorUpdateContactRolesMutation,
  UpdateByIdUserInput,
} from "gql/generated";
import { Dispatch, SetStateAction, useState } from "react";
import { useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { ApolloQueryResult } from "@apollo/client";
import { IInviteStaffMember } from "shared/components/molecules";
import { defaultHiddenEmailStart } from "shared/utils";
import { usePostHog } from "shared/hooks";

export enum TagAction {
  Delete = "DELETE",
  Remove = "REMOVE",
}

export type EmailInviteInfo = {
  email: string;
  firstName: string;
  lastName: string;
  photo?: string;
  title?: string;
  role?: string;
  contactRoles?: ContactRole[];
};

type InviteForm = UseFormReturn<{
  photo?: string;
  email?: string;
  firstName: string;
  lastName: string;
  title?: string;
  role: EnumUserAccessOperatorsRole;
  contactRoles: ContactRole[];
  sendInvitationEmail: boolean;
}>;

export type UseInviteStaffProps = {
  _entityId: string;
  type: EnumEntityReferenceType;
  setUpdatingEmail: Dispatch<SetStateAction<string | undefined>>;
  refetch:
    | ((
        variables?:
          | Partial<
              Exact<{
                organizationId: Scalars["ID"]["input"];
              }>
            >
          | undefined,
      ) => Promise<ApolloQueryResult<OrganizationStaffQuery>>)
    | ((
        variables?:
          | Partial<
              Exact<{
                operatorId: Scalars["ID"]["input"];
              }>
            >
          | undefined,
      ) => Promise<ApolloQueryResult<OperatorStaffQuery>>);
  onClose: () => void;
  onInviteComplete?: () => Promise<void> | void;
  user?: Partial<UserWithPictureTc>;
};

export const useInviteStaff = ({
  _entityId,
  type,
  setUpdatingEmail,
  refetch,
  onClose,
  onInviteComplete,
  user,
}: UseInviteStaffProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [confirmModalInfo, setConfirmModalInfo] = useState<
    | {
        action: TagAction;
        userId: string;
        email: string;
        primaryContactReplacementUserId?: string;
      }
    | undefined
  >();
  const handleOpenConfirmationModal = (
    action: TagAction,
    userId: string,
    email: string,
    primaryContactReplacementUserId?: string,
  ) => {
    setConfirmModalInfo({
      action,
      userId,
      email,
      primaryContactReplacementUserId,
    });
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmationModalActionClick = (confirmed: boolean) => {
    if (confirmed && confirmModalInfo) {
      handleRemoveClick(confirmModalInfo.userId, confirmModalInfo.email);
      onClose();
    }
    setIsConfirmationModalOpen(false);
  };

  /**
   * Handle closing the modal and resetting the form
   * @param form
   * @param defaultValues
   */
  const handleCloseClick = (
    form: InviteForm,
    defaultValues: InviteStaffMemberFormValues,
  ) => {
    form.reset(defaultValues);
    onClose();
  };

  // The mutation to create or resend an invite use the same params
  const [createOrResendInvite] = useUserCreateInviteMutation();

  // When a user access request is accepted
  const [acceptAccessRequest] = useUserApproveAccessMutation();

  // When a user is removed, we need to refetch the data to get the updated list
  const [revokeInvite] = useUserRevokeInviteMutation();

  /**
   * Accept the user's request to join the business
   * @param id
   */
  const handleAcceptClick = async (id: string, email: string) => {
    setUpdatingEmail(email);
    try {
      await acceptAccessRequest({
        variables: {
          userId: id,
          entity: {
            _entityId: _entityId,
            type,
          },
        },
        refetchQueries: [
          type === EnumEntityReferenceType.Operator
            ? OperatorStaffDocument
            : OrganizationStaffDocument,
        ],
      });
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("core:STAFF.SUCCESS.ACCEPTED", { email }),
      });
    } catch {
      trackPostHogException(new Error("Error accepting staff member"), {
        fileName: "useInviteStaff.ts",
        method: "handleAcceptClick",
        message: "Error accepting staff member",
      });
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:STAFF.ERROR.ACCEPTING", {
          email: id,
        }),
      });
    } finally {
      setUpdatingEmail(undefined);
    }
  };

  /**
   * Handle creating staff invites
   * - These are sent one at a time, instead of a group, so we can get a toast notification
   *   for each staff member that is being added
   * @param staffToInvite
   */
  const handleInviteStaffClick = async (personToAdd: IInviteStaffMember) => {
    await handleCreateOrResendInviteClick(
      personToAdd,
      true,
      personToAdd.sendInvitationEmail,
    );
    await refetch();
  };

  /**
   * Remove the staff member and refetch the data
   * @param id
   */
  const handleRemoveClick = async (id: string, email: string) => {
    setUpdatingEmail(email);
    try {
      await revokeInvite({
        variables: {
          userId: id,
          entity: {
            _entityId: _entityId,
            type,
          },
          primaryContactReplacementUserId:
            confirmModalInfo?.primaryContactReplacementUserId,
        },
        refetchQueries: [
          type === EnumEntityReferenceType.Operator
            ? OperatorStaffDocument
            : OrganizationStaffDocument,
        ],
      });
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("core:STAFF.SUCCESS.REMOVED", { email }),
      });
    } catch {
      trackPostHogException(new Error("Error removing staff member"), {
        fileName: "useInviteStaff.ts",
        method: "handleRemoveClick",
        message: "Error removing staff member",
      });
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:STAFF.ERROR.REMOVING", { email }),
      });
    } finally {
      setUpdatingEmail(undefined);
    }
  };

  /**
   * Creates a new invite OR resends an invite
   * @param param0
   */
  const handleCreateOrResendInviteClick = async (
    {
      email,
      firstName,
      lastName,
      photo,
      title,
      role,
      contactRoles,
    }: EmailInviteInfo,
    isCreating: boolean = false,
    sendInvitationEmail: boolean,
  ) => {
    setUpdatingEmail(email);
    try {
      // If we're resending an invite for an existing user and the email has changed,
      // we need to update the user's email first to avoid creating duplicates
      if (!isCreating && user && user._id && user.email && user.email !== email) {
        await updateUser({
          variables: {
            id: user._id,
            record: {
              email,
            },
          },
        });
      }

      await createOrResendInvite({
        variables: {
          email,
          firstName,
          lastName,
          ...(photo && { photo }),
          title,
          sendInvitationEmail,
          entity: {
            _entityId,
            type,
          },
          role: role ? (role as RoleType) : undefined,
          contactRoles:
            type === EnumEntityReferenceType.Operator ? contactRoles : undefined,
        },
        refetchQueries: [
          type === EnumEntityReferenceType.Operator
            ? OperatorStaffDocument
            : OrganizationStaffDocument,
        ],
      });
      await onInviteComplete?.();
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: sendInvitationEmail
          ? t("core:STAFF.SUCCESS.STAFF_INVITED", {
              email: email || firstName + " " + lastName,
            })
          : t("core:STAFF.SUCCESS.STAFF_ADDED", {
              email: email || firstName + " " + lastName,
            }),
      });
    } catch {
      trackPostHogException(
        new Error("Error creating or resending staff member"),
        {
          fileName: "useInviteStaff.ts",
          method: "handleCreateOrResendInviteClick",
          message: "Error creating or resending staff member",
        },
      );
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: isCreating
          ? t("core:STAFF.ERROR.INVITING", {
              email: email || firstName + " " + lastName,
            })
          : t("core:STAFF.ERROR.RESENDING", {
              email: email || firstName + " " + lastName,
            }),
      });
    } finally {
      setUpdatingEmail(undefined);
    }
  };

  const [updateUser, { loading: isUpdatingUser }] = useUserUpdateMutation();
  const [updateContactRoles] = useOperatorUpdateContactRolesMutation();

  const handleUpdateAvatarOrRoleClick = async (
    user: Partial<UserWithPictureTc>,
    formValues: InviteStaffMemberFormValues,
    currentRole: EnumUserAccessOperatorsRole,
  ): Promise<boolean> => {
    const emailChanged =
      formValues.email &&
      user.email !== formValues.email &&
      !formValues.email?.startsWith(defaultHiddenEmailStart);
    const photoChanged = user.photo?._id !== formValues.photo && !!formValues.photo;
    const roleChanged = currentRole !== formValues.role;
    const contactRolesChanged =
      JSON.stringify(user.contactRoles ?? []) !==
      JSON.stringify(formValues.contactRoles ?? []);
    const titleChanged = (user.title ?? "") !== (formValues.title ?? "");
    let updateStep = "build-record";

    try {
      const record: UpdateByIdUserInput = {
        // If the email has changed, update the email
        ...(emailChanged ? { email: formValues.email } : {}),
        // If the photo has changed, update the photo
        ...(user.photo?._id !== formValues.photo && formValues.photo
          ? { photo: formValues.photo }
          : {}),
        // If the role has changed, update the role
        ...(currentRole !== formValues.role && {
          access: {
            ...user.access,
            ...(type === EnumEntityReferenceType.Operator
              ? {
                  operators: user.access?.operators?.map(item =>
                    item?.entityId === _entityId
                      ? {
                          ...item,
                          role: formValues.role as unknown as RoleType,
                        }
                      : item,
                  ) as UserAccessOperators[],
                }
              : type === EnumEntityReferenceType.Organization
                ? {
                    organizations: user.access?.organizations?.map(item =>
                      item?.entityId === _entityId
                        ? {
                            ...item,
                            role: formValues.role as unknown as RoleType,
                          }
                        : item,
                    ) as UserAccessOperators[],
                  }
                : type === EnumEntityReferenceType.Storefront
                  ? {
                      storefronts: user.access?.storefronts?.map(item =>
                        item?.entityId === _entityId
                          ? {
                              ...item,
                              role: formValues.role as unknown as RoleType,
                            }
                          : item,
                      ) as UserAccessOperators[],
                    }
                  : {}),
          },
        }),
      };

      if (Object.keys(record).length > 0) {
        updateStep = "userUpdate";
        const response = await updateUser({
          variables: {
            id: user._id,
            record,
          },
          refetchQueries: [
            type === EnumEntityReferenceType.Operator
              ? OperatorStaffDocument
              : OrganizationStaffDocument,
          ],
        });
        if (response.errors) {
          throw new Error(response.errors[0].message);
        }
      }
      if (type === EnumEntityReferenceType.Operator && user._id) {
        updateStep = "operatorUpdateContactRoles";
        await updateContactRoles({
          variables: {
            operatorEntityId: _entityId,
            userId: user._id,
            contactRoles: formValues.contactRoles ?? [],
            title: formValues.title ?? "",
            entity: {
              _entityId,
              type,
            },
          },
          refetchQueries: [OperatorStaffDocument],
        });
      }
      updateStep = "refetchStaff";
      await refetch();
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("core:STAFF.SUCCESS.UPDATED", {
          email: !user.email?.startsWith(defaultHiddenEmailStart)
            ? formValues.email ||
              formValues.firstName + " " + formValues.lastName
            : formValues.firstName + " " + formValues.lastName,
        }),
      });
      return true;
    } catch (error) {
      trackPostHogException(error, {
        fileName: "useInviteStaff.ts",
        method: "handleUpdateAvatarOrRoleClick",
        message: "Error updating staff member",
        extra: {
          updateStep,
          entityId: _entityId,
          entityType: type,
          updatingUserId: user._id ?? "",
          emailChanged: !!emailChanged,
          photoChanged,
          roleChanged,
          contactRolesChanged,
          titleChanged,
        },
      });
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:STAFF.ERROR.UPDATING", {
          email: !user.email?.startsWith(defaultHiddenEmailStart)
            ? formValues.email ||
              formValues.firstName + " " + formValues.lastName
            : formValues.firstName + " " + formValues.lastName,
        }),
      });
      return false;
    }
  };

  return {
    confirmModalInfo,
    isConfirmationModalOpen,
    isUpdatingUser,
    setIsConfirmationModalOpen,
    handleCloseClick,
    handleConfirmationModalActionClick,
    handleOpenConfirmationModal,
    handleAcceptClick,
    handleInviteStaffClick,
    handleCreateOrResendInviteClick,
    handleUpdateAvatarOrRoleClick,
  };
};
